import React, { ChangeEvent, useMemo } from 'react';

import { MenuItem, Stack, TextField } from '@mui/material';

import {
  EMAIL_DELIVERY_FAILURE_RESOLUTION_TYPES,
  EMAIL_DELIVERY_FAILURE_RESOLUTION_TYPE_LABELS,
  EMAIL_REGEX,
  EmailDeliveryFailureResolutionType,
} from './constants';

interface Props {
  resolutionType: EmailDeliveryFailureResolutionType | '';
  onResolutionTypeChange: (value: EmailDeliveryFailureResolutionType) => void;
  notes: string;
  onNotesChange: (value: string) => void;
  forwardedToEmail?: string;
  onForwardedToEmailChange?: (value: string) => void;
  resolutionTypeOptions?: Array<EmailDeliveryFailureResolutionType>;
  disableResolutionType?: boolean;
  disableForwardedToEmail?: boolean;
}

export default function EmailDeliveryFailureResolutionFields({
  resolutionType,
  onResolutionTypeChange,
  notes,
  onNotesChange,
  forwardedToEmail = '',
  onForwardedToEmailChange,
  resolutionTypeOptions = [...EMAIL_DELIVERY_FAILURE_RESOLUTION_TYPES],
  disableResolutionType = false,
  disableForwardedToEmail = false,
}: Props) {
  const showForwardedToEmail = resolutionType === 'FORWARD';

  const forwardedToEmailHelperText = useMemo(() => {
    if (!showForwardedToEmail) {
      return '';
    }

    if (!forwardedToEmail) {
      return 'Forward to email is required for FORWARD resolutions';
    }

    if (!EMAIL_REGEX.test(forwardedToEmail)) {
      return 'Please enter a valid email address';
    }

    return '';
  }, [forwardedToEmail, showForwardedToEmail]);

  const isForwardedToEmailInvalid = showForwardedToEmail && (!forwardedToEmail || !EMAIL_REGEX.test(forwardedToEmail));

  const handleResolutionTypeChange = (event: ChangeEvent<HTMLInputElement>) => {
    onResolutionTypeChange(event.target.value as EmailDeliveryFailureResolutionType);
  };

  const handleNotesChange = (event: ChangeEvent<HTMLInputElement>) => {
    onNotesChange(event.target.value);
  };

  const handleForwardedToEmailChange = (event: ChangeEvent<HTMLInputElement>) => {
    onForwardedToEmailChange?.(event.target.value);
  };

  return (
    <Stack spacing={2} sx={{ mt: 1 }}>
      <TextField
        select
        fullWidth
        required
        label="Resolution type"
        value={resolutionType}
        onChange={handleResolutionTypeChange}
        disabled={disableResolutionType}
      >
        {resolutionTypeOptions.map((option) => (
          <MenuItem key={option} value={option}>
            {EMAIL_DELIVERY_FAILURE_RESOLUTION_TYPE_LABELS[option]}
          </MenuItem>
        ))}
      </TextField>

      {showForwardedToEmail && (
        <TextField
          fullWidth
          required
          label="Forward to"
          type="email"
          value={forwardedToEmail}
          onChange={handleForwardedToEmailChange}
          disabled={disableForwardedToEmail}
          error={isForwardedToEmailInvalid}
          helperText={forwardedToEmailHelperText}
        />
      )}

      <TextField
        fullWidth
        multiline
        minRows={3}
        label="Notes"
        value={notes}
        onChange={handleNotesChange}
        placeholder="Add any context for this resolution"
      />
    </Stack>
  );
}
